import React, { useCallback, useEffect, useState } from 'react';
import {
  DollarSign, Link2, Truck, RefreshCw, Save, Plus,
  MapPin, Route, Navigation, CheckCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import LiveDriverMap from './LiveDriverMap';
import GooglePlacesAddressInput from './GooglePlacesAddressInput';
import { getCommerceTheme } from './commerceTheme';

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'JPY', 'AED', 'SGD', 'MXN'];

function resolveCommerceSection(section) {
  if (section === 'language') return 'currency';
  return section || 'currency';
}

const CommerceExtensionsPanel = ({
  websiteId,
  token,
  API_URL,
  orders = [],
  onRefresh,
  initialSection = 'currency',
  embedded = false,
  theme = 'dark',
  onSectionChange,
}) => {
  const s = getCommerceTheme(theme);
  const showTabs = theme === 'dash' || !embedded;
  const [section, setSection] = useState(resolveCommerceSection(initialSection));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({});
  const [integrations, setIntegrations] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [mapsApiKey, setMapsApiKey] = useState('');
  const [driverForm, setDriverForm] = useState({ name: '', phone: '', email: '', vehicle: '' });
  const [addingDriver, setAddingDriver] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const selectSection = (id) => {
    setSection(id);
    onSectionChange?.(id);
  };

  const handleDepotAddressChange = useCallback((formatted, parsed) => {
    setConfig((prev) => {
      const next = {
        ...prev,
        depot_address: formatted || parsed?.formatted_address || '',
      };
      if (parsed?.lat != null && parsed?.lng != null) {
        next.depot_lat = parsed.lat;
        next.depot_lng = parsed.lng;
      } else if (!parsed?.place_id) {
        next.depot_lat = 0;
        next.depot_lng = 0;
      }
      return next;
    });
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [cfgRes, intRes, drvRes, routeRes] = await Promise.all([
        fetch(`${API_URL}/api/websites/${websiteId}/commerce/config`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/commerce/integrations`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/commerce/drivers`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/commerce/routes`, { headers }),
      ]);
      if (cfgRes.ok) {
        const data = await cfgRes.json();
        setConfig(data.settings || {});
        setMapsApiKey(data.google_maps_api_key || '');
      }
      if (intRes.ok) setIntegrations(await intRes.json());
      if (drvRes.ok) {
        const driverRows = await drvRes.json();
        setDrivers(Array.isArray(driverRows) ? driverRows : []);
      }
      if (routeRes.ok) setRoutes(await routeRes.json());
    } catch (e) {
      toast.error('Failed to load commerce extensions');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [websiteId]);

  useEffect(() => {
    if (initialSection) setSection(resolveCommerceSection(initialSection));
  }, [initialSection]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/commerce/config`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(config),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Commerce settings saved');
      load();
      onRefresh?.();
    } catch (e) {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const saveIntegrations = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/commerce/integrations`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(integrations),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Integration settings saved');
      load();
    } catch (e) {
      toast.error('Failed to save integrations');
    }
    setSaving(false);
  };

  const testIntegration = async () => {
    try {
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/commerce/integrations/test`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.detail || data.message || 'Test failed');
      if (data.mode === 'hybrid') {
        toast.success('Hybrid integration checked. At least one source is connected.');
      } else {
        toast.success(`Connected${data.shop_name ? `: ${data.shop_name}` : ''}`);
      }
    } catch (e) {
      toast.error(e.message || 'Connection test failed');
    }
  };

  const syncCatalog = async () => {
    try {
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/commerce/integrations/sync`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Sync failed');
      toast.success(`Synced ${data.imported} products from ${data.source}`);
      onRefresh?.();
      load();
    } catch (e) {
      toast.error(e.message || 'Sync failed');
    }
  };

  const addDriver = async () => {
    if (!driverForm.name.trim()) {
      toast.error('Driver name is required');
      return;
    }
    if (!token) {
      toast.error('You must be signed in to add drivers');
      return;
    }
    setAddingDriver(true);
    try {
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/commerce/drivers`, {
        method: 'POST',
        headers,
        body: JSON.stringify(driverForm),
      });
      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = { detail: raw || 'Create failed' };
      }
      if (!res.ok) throw new Error(data.detail || data.message || `Create failed (${res.status})`);
      toast.success(`Driver added: ${data.name || driverForm.name}`);
      if (data.driver_token) {
        const appUrl = `${window.location.origin}/driver/${websiteId}?token=${encodeURIComponent(data.driver_token)}`;
        toast.success('Driver app link copied to clipboard', { duration: 8000 });
        try {
          await navigator.clipboard.writeText(appUrl);
        } catch {
          toast.info(`Driver app: ${appUrl}`, { duration: 15000 });
        }
      }
      setDriverForm({ name: '', phone: '', email: '', vehicle: '' });
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to add driver');
    }
    setAddingDriver(false);
  };

  const issueDriverLink = async (driverId, driverName) => {
    try {
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/commerce/drivers/${driverId}/issue-token`, {
        method: 'POST',
        headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not issue driver link');
      const appUrl = `${window.location.origin}${data.driver_app_url || `/driver/${websiteId}?token=${data.driver_token}`}`;
      try {
        await navigator.clipboard.writeText(appUrl);
        toast.success(`Driver app link copied for ${driverName}`);
      } catch {
        toast.info(`Driver app: ${appUrl}`, { duration: 15000 });
      }
    } catch (e) {
      toast.error(e.message || 'Failed to get driver link');
    }
  };

  const optimizeRoute = async () => {
    if (!selectedOrders.length) {
      toast.error('Select at least one order for delivery');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/commerce/routes/optimize`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          order_ids: selectedOrders,
          driver_id: selectedDriverId || undefined,
          auto_activate: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Optimization failed');
      toast.success(`Route planned — ${data.total_distance_km} km, ~${data.estimated_minutes} min`);
      setSelectedOrders([]);
      load();
      onRefresh?.();
    } catch (e) {
      toast.error(e.message || 'Route optimization failed');
    }
  };

  const toggleOrder = (id) => {
    setSelectedOrders((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const sections = [
    { id: 'currency', label: 'Multi-Currency', icon: DollarSign },
    { id: 'integrations', label: 'Shopify / Woo', icon: Link2 },
    { id: 'delivery', label: 'Delivery & Routes', icon: Truck },
  ];

  const deliveryOrders = (orders || []).filter(
    (o) => o.fulfillment_type === 'local_delivery' || o.shipping_address?.address1 || o.delivery_lat,
  );

  if (loading) {
    return <div className={s.loading}>Loading commerce extensions…</div>;
  }

  return (
    <div className={s.page}>
      {showTabs && (
        <div className="flex flex-wrap gap-2">
          {sections.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => selectSection(id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition ${
                section === id ? s.tabActive : s.tabInactive
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      )}

      {section === 'currency' && (
        <div className={s.card}>
          <h3 className={s.title}>Multi-Currency</h3>
          <p className={s.muted}>Set base currency and enabled storefront currencies. Prices auto-convert unless manual per-currency prices are set on products.</p>
          <div className={s.infoBanner}>
            These values sync to Website Settings and the template editor preview after Save.
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={s.label}>Base currency</label>
              <select
                value={config.base_currency || config.currency || 'USD'}
                onChange={(e) => setConfig({ ...config, base_currency: e.target.value, currency: e.target.value })}
                className={s.input}
              >
                {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 mt-6">
              <input
                type="checkbox"
                checked={config.auto_convert_prices !== false}
                onChange={(e) => setConfig({ ...config, auto_convert_prices: e.target.checked })}
              />
              <span className="text-sm">Auto-convert prices from base currency</span>
            </label>
          </div>
          <div>
            <label className={s.labelBlock}>Enabled currencies</label>
            <div className="flex flex-wrap gap-2">
              {CURRENCY_OPTIONS.map((c) => {
                const enabled = (config.enabled_currencies || ['USD']).includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      const list = config.enabled_currencies || ['USD'];
                      setConfig({
                        ...config,
                        enabled_currencies: enabled ? list.filter((x) => x !== c) : [...list, c],
                      });
                    }}
                    className={`px-3 py-1 rounded-full text-xs border ${enabled ? s.currencyChipOn : s.currencyChipOff}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <Button onClick={saveConfig} disabled={saving} className={s.primaryBtn}>
            <Save className="w-4 h-4 mr-2" />Save currency settings
          </Button>
        </div>
      )}

      {section === 'integrations' && (
        <div className={s.card}>
          <h3 className={s.title}>Shopify & WooCommerce</h3>
          <p className={s.muted}>Save credentials, test the connection, then sync catalog. Imported products appear in Operate and the template editor product grids.</p>
          <div>
            <label className={s.label}>Commerce mode</label>
            <select
              value={integrations.mode || 'native'}
              onChange={(e) => setIntegrations({ ...integrations, mode: e.target.value })}
              className={s.inputMd}
            >
              <option value="native">Native Snagcart store</option>
              <option value="shopify">Shopify catalog sync</option>
              <option value="woocommerce">WooCommerce catalog sync</option>
              <option value="hybrid">Hybrid (sync + native checkout)</option>
            </select>
          </div>

          {(integrations.mode === 'shopify' || integrations.mode === 'hybrid') && (
            <div className="grid md:grid-cols-2 gap-4 pt-2">
              <input
                placeholder="my-store.myshopify.com"
                value={integrations.shopify_shop_domain || ''}
                onChange={(e) => setIntegrations({ ...integrations, shopify_shop_domain: e.target.value })}
                className={s.inputInline}
              />
              <input
                type="password"
                placeholder={integrations.shopify_access_token_set ? '•••••••• (unchanged)' : 'Shopify Admin API access token'}
                onChange={(e) => setIntegrations({ ...integrations, shopify_access_token: e.target.value })}
                className={s.inputInline}
              />
            </div>
          )}

          {(integrations.mode === 'woocommerce' || integrations.mode === 'hybrid') && (
            <div className="grid md:grid-cols-2 gap-4 pt-2">
              <input
                placeholder="https://yourstore.com"
                value={integrations.woocommerce_store_url || ''}
                onChange={(e) => setIntegrations({ ...integrations, woocommerce_store_url: e.target.value })}
                className={`${s.inputInline} md:col-span-2`}
              />
              <input
                placeholder="WooCommerce consumer key"
                value={integrations.woocommerce_consumer_key || ''}
                onChange={(e) => setIntegrations({ ...integrations, woocommerce_consumer_key: e.target.value })}
                className={s.inputInline}
              />
              <input
                type="password"
                placeholder={integrations.woocommerce_consumer_secret_set ? '••••••••' : 'WooCommerce consumer secret'}
                onChange={(e) => setIntegrations({ ...integrations, woocommerce_consumer_secret: e.target.value })}
                className={s.inputInline}
              />
            </div>
          )}

          {integrations.last_sync_at && (
            <p className={s.meta}>Last sync: {integrations.last_sync_at} ({integrations.last_sync_count || 0} products)</p>
          )}

          <div className="flex flex-wrap gap-3">
            <Button onClick={saveIntegrations} variant="outline" className={s.outlineBtn}>
              <Save className="w-4 h-4 mr-2" />Save integration
            </Button>
            <Button onClick={testIntegration} variant="outline" className={s.outlineBtn}>
              <CheckCircle className="w-4 h-4 mr-2" />Test connection
            </Button>
            <Button onClick={syncCatalog} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <RefreshCw className="w-4 h-4 mr-2" />Sync catalog
            </Button>
          </div>
        </div>
      )}

      {section === 'delivery' && (
        <div className="space-y-6">
          <div className={s.card}>
            <h3 className={`${s.title} flex items-center gap-2`}><MapPin className="w-5 h-5" />Delivery depot</h3>
            <p className={s.muted}>Search your depot on Google Maps — coordinates resolve automatically when you save. No manual latitude/longitude needed.</p>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!config.delivery_enabled}
                onChange={(e) => setConfig({ ...config, delivery_enabled: e.target.checked })}
              />
              <span className="text-sm">Enable local delivery & live tracking</span>
            </label>
            {config.depot_address && !config.delivery_enabled && (
              <p className={s.warnBanner}>
                Depot address is set but delivery is off — checkout will not show local delivery until you enable it above and save.
              </p>
            )}
            {config.delivery_enabled && !mapsApiKey && (
              <p className={s.warnBanner}>
                No Google Maps API key detected — customers can type a full address at checkout. Add GOOGLE_MAPS_API_KEY in Settings → API Keys (enable Places API).
              </p>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={s.label}>Depot address</label>
                <GooglePlacesAddressInput
                  key={mapsApiKey ? `depot-${mapsApiKey.slice(0, 8)}` : 'depot-no-key'}
                  apiKey={mapsApiKey}
                  value={config.depot_address || ''}
                  onChange={handleDepotAddressChange}
                  placeholder="Search depot / store address on Google Maps"
                  className={s.placesInput}
                  theme={theme}
                />
              </div>
              <div>
                <label className={s.label}>Delivery radius (km)</label>
                <input
                  type="number"
                  placeholder="Radius (km)"
                  value={config.local_delivery_radius_km || 25}
                  onChange={(e) => setConfig({ ...config, local_delivery_radius_km: parseFloat(e.target.value) || 25 })}
                  className={s.input}
                />
              </div>
              {config.depot_lat && config.depot_lng ? (
                <div className="flex items-end">
                  <p className={s.successText}>
                    Depot synced: {Number(config.depot_lat).toFixed(5)}, {Number(config.depot_lng).toFixed(5)}
                  </p>
                </div>
              ) : config.depot_address ? (
                <div className="flex items-end">
                  <p className={s.warnText}>Save to sync coordinates from this address.</p>
                </div>
              ) : null}
            </div>
            <Button onClick={saveConfig} disabled={saving} className={s.primaryBtn}>
              <Save className="w-4 h-4 mr-2" />Save delivery settings
            </Button>
          </div>

          <div className={s.card}>
            <h3 className={s.title}>Drivers</h3>
            <p className={s.muted}>
              Add drivers, then share the driver app link. Drivers open it on their phone, tap Start GPS, and their location appears in Live Driver Tracking below.
            </p>
            <div className="grid md:grid-cols-5 gap-3">
              <input placeholder="Name *" value={driverForm.name} onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })} className={s.inputInline} />
              <input placeholder="Phone" value={driverForm.phone} onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })} className={s.inputInline} />
              <input placeholder="Email" value={driverForm.email} onChange={(e) => setDriverForm({ ...driverForm, email: e.target.value })} className={s.inputInline} />
              <input placeholder="Vehicle" value={driverForm.vehicle} onChange={(e) => setDriverForm({ ...driverForm, vehicle: e.target.value })} className={s.inputInline} />
              <Button type="button" onClick={addDriver} disabled={addingDriver} className={s.addDriverBtn}>
                <Plus className="w-4 h-4 mr-1" />{addingDriver ? 'Adding…' : 'Add'}
              </Button>
            </div>
            <div className="space-y-2">
              {drivers.map((d) => (
                <div key={d.id} className={s.row}>
                  <span className="min-w-0 truncate">{d.name} {d.phone && `· ${d.phone}`}{d.vehicle && ` · ${d.vehicle}`}</span>
                  <span className={s.metaInline}>{d.last_lat ? `${d.last_lat.toFixed(4)}, ${d.last_lng?.toFixed(4)}` : 'No GPS yet'}</span>
                  <Button type="button" size="sm" variant="outline" className={s.driverLinkBtn} onClick={() => issueDriverLink(d.id, d.name)}>
                    Driver app link
                  </Button>
                </div>
              ))}
              {!drivers.length && <p className={s.empty}>No drivers yet.</p>}
            </div>
          </div>

          <div className={s.card}>
            <h3 className={`${s.title} flex items-center gap-2`}><Route className="w-5 h-5" />Route optimization</h3>
            <p className={s.muted}>Select delivery orders (geocoded when customers checkout). Routes auto-activate for live tracking.</p>
            <div className="max-w-md">
              <label className={s.label}>Assign driver (optional)</label>
              <select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                className={`${s.input} text-sm`}
              >
                <option value="">Unassigned</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {deliveryOrders.slice(0, 30).map((o) => (
                <label key={o.id} className={s.orderRow}>
                  <input type="checkbox" checked={selectedOrders.includes(o.id)} onChange={() => toggleOrder(o.id)} />
                  <span className="min-w-0">
                    {o.order_number || o.id.slice(0, 8)} — {o.customer_name || o.customer_email}
                    {o.shipping_address?.formatted_address || o.shipping_address?.address1 ? (
                      <span className={`block text-xs truncate ${theme === 'dash' ? 'text-slate-500' : 'text-zinc-500'}`}>
                        {o.shipping_address.formatted_address || o.shipping_address.address1}
                      </span>
                    ) : null}
                  </span>
                  <span className={`ml-auto shrink-0 ${s.routeMeta}`}>{o.delivery_status || o.status}</span>
                </label>
              ))}
              {!deliveryOrders.length && (
                <p className={s.empty}>No delivery orders yet. Customers must choose local delivery at checkout.</p>
              )}
            </div>
            <Button onClick={optimizeRoute} className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <Navigation className="w-4 h-4 mr-2" />Optimize route ({selectedOrders.length} orders)
            </Button>
          </div>

          <LiveDriverMap
            websiteId={websiteId}
            token={token}
            API_URL={API_URL}
            drivers={drivers}
            routes={routes}
            theme={theme}
          />

          <div className={s.card}>
            <h3 className={`${s.title} mb-3`}>Recent routes</h3>
            {routes.length === 0 ? (
              <p className={s.empty}>No routes planned yet.</p>
            ) : (
              <div className="space-y-2">
                {routes.slice(0, 8).map((r) => (
                  <div key={r.id} className={s.routeRow}>
                    <span>{r.driver_name || 'Unassigned'} · {(r.stops || []).length} stops</span>
                    <span className={s.routeMeta}>{r.total_distance_km} km · ~{r.estimated_minutes} min · {r.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommerceExtensionsPanel;
